import React, { useState, useEffect } from 'react';
import {
  VideoCameraIcon,
  CheckCircleIcon,
  XCircleIcon,
  LinkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function ZoomConnection() {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [showSetupForm, setShowSetupForm] = useState(false);
  const [zoomConfig, setZoomConfig] = useState({
    clientId: '',
    clientSecret: '',
    redirectUri: 'http://localhost:3006/oauth/callback'
  });

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const response = await fetch('/api/v1/zoomfabric/connection/status');
      if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data.connected);
        setAccountInfo(data.account);
      }
    } catch (err) {
      console.error('Failed to check connection status:', err);
      setConnectionStatus(false);
    }
  };

  const saveZoomConfig = async () => {
    if (!zoomConfig.clientId || !zoomConfig.clientSecret) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/v1/zoomfabric/connection/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(zoomConfig)
      });

      if (response.ok) {
        setShowSetupForm(false);
        alert('Zoom app configuration saved! You can now connect to Zoom.');
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (err) {
      console.error('Failed to save Zoom config:', err);
      alert('Failed to save configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const initiateZoomAuth = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/zoomfabric/connection/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(zoomConfig)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        } else {
          throw new Error(data.error || 'Failed to get authorization URL');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initiate OAuth');
      }
    } catch (err) {
      console.error('Failed to initiate Zoom auth:', err);
      alert('Failed to initiate Zoom authentication. Please check your app configuration.');
    } finally {
      setLoading(false);
    }
  };

  const disconnectZoom = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/zoomfabric/connection/disconnect', {
        method: 'DELETE'
      });

      if (response.ok) {
        setConnectionStatus(false);
        setAccountInfo(null);
      }
    } catch (err) {
      console.error('Failed to disconnect Zoom:', err);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/zoomfabric/connection/test');
      if (response.ok) {
        await checkConnectionStatus();
      }
    } catch (err) {
      console.error('Failed to test connection:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Zoom Connection</h1>
        <p className="text-lg text-gray-600 mt-2">
          Connect your Zoom workspace to access meeting and phone call transcripts and recordings
        </p>
      </div>

      {/* Connection Status Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Connection Status</h3>
            <button
              onClick={testConnection}
              disabled={loading}
              className="flex items-center px-3 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Test Connection
            </button>
          </div>

          {connectionStatus === null ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Checking connection status...</span>
            </div>
          ) : connectionStatus ? (
            <div>
              <div className="flex items-center mb-4">
                <CheckCircleIcon className="w-6 h-6 text-green-500" />
                <span className="ml-3 text-green-700 font-medium">Connected to Zoom</span>
              </div>

              {accountInfo && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Account Information</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>Account Name: {accountInfo.account_name}</div>
                    <div>Account Type: {accountInfo.account_type}</div>
                    <div>License: {accountInfo.license_type}</div>
                    <div>Connected: {new Date(accountInfo.connected_at).toLocaleDateString()}</div>
                  </div>
                </div>
              )}

              <button
                onClick={disconnectZoom}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Disconnecting...' : 'Disconnect Zoom'}
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center mb-4">
                <XCircleIcon className="w-6 h-6 text-red-500" />
                <span className="ml-3 text-red-700 font-medium">Not connected to Zoom</span>
              </div>

              <p className="text-gray-600 mb-4">
                Connect your Zoom workspace to access meeting and phone call recordings and transcripts for analysis.
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowSetupForm(true)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 flex items-center text-sm"
                >
                  Setup Zoom App
                </button>
                <button
                  onClick={initiateZoomAuth}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  <LinkIcon className="w-5 h-5 mr-2" />
                  {loading ? 'Connecting...' : 'Connect to Zoom'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Zoom App Setup Modal */}
      {showSetupForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Setup Zoom App</h2>
                <button
                  onClick={() => setShowSetupForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* Instructions */}
              <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h3 className="text-lg font-medium text-yellow-900 mb-2">Before You Start</h3>
                <ol className="text-sm text-yellow-800 space-y-2 list-decimal list-inside">
                  <li>Go to <a href="https://marketplace.zoom.us/develop/create" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Zoom App Marketplace</a></li>
                  <li>Click "Create" and select "Server-to-Server OAuth" app type</li>
                  <li>Fill out basic app information (name, description, etc.)</li>
                  <li>In "Scopes", add these permissions:
                    <div className="mt-1 space-y-1">
                      <div><code className="bg-gray-100 px-1 rounded text-xs">recording:read:admin</code> - Access meeting recordings</div>
                      <div><code className="bg-gray-100 px-1 rounded text-xs">phone:read:admin</code> - Access phone recordings</div>
                      <div><code className="bg-gray-100 px-1 rounded text-xs">phone:read:call_logs</code> - Access call logs</div>
                      <div><code className="bg-gray-100 px-1 rounded text-xs">user:read:admin</code> - Access user information</div>
                    </div>
                  </li>
                  <li>Add redirect URL: <code className="bg-gray-100 px-1 rounded">http://localhost:3006/oauth/callback</code></li>
                  <li>Copy your Client ID and Client Secret from the app credentials</li>
                </ol>
              </div>

              {/* Configuration Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client ID *</label>
                  <input
                    type="text"
                    value={zoomConfig.clientId}
                    onChange={(e) => setZoomConfig({...zoomConfig, clientId: e.target.value})}
                    placeholder="Enter your Zoom app Client ID"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret *</label>
                  <input
                    type="password"
                    value={zoomConfig.clientSecret}
                    onChange={(e) => setZoomConfig({...zoomConfig, clientSecret: e.target.value})}
                    placeholder="Enter your Zoom app Client Secret"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Redirect URI</label>
                  <input
                    type="text"
                    value={zoomConfig.redirectUri}
                    onChange={(e) => setZoomConfig({...zoomConfig, redirectUri: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md bg-gray-50"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">This should match the redirect URI in your Zoom app settings</p>
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-3">
                <button
                  onClick={() => setShowSetupForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveZoomConfig}
                  disabled={loading || !zoomConfig.clientId || !zoomConfig.clientSecret}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Requirements and Information */}
      <div className="bg-blue-50 rounded-lg p-6">
        <div className="flex">
          <VideoCameraIcon className="w-6 h-6 text-blue-600 flex-shrink-0" />
          <div className="ml-3">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Requirements</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Zoom Pro, Business, or Enterprise account required</li>
              <li>• Cloud recording must be enabled in your Zoom settings</li>
              <li>• Zoom Phone license required for phone call analysis</li>
              <li>• Account admin approval may be required for OAuth connection</li>
              <li>• Transcription must be enabled for meetings and calls you want to analyze</li>
            </ul>

            <h4 className="font-medium text-blue-900 mt-4 mb-2">What ZoomFabric can access:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Meeting recordings and transcripts from your cloud storage</li>
              <li>• Phone call recordings and transcripts from Zoom Phone</li>
              <li>• Call logs and metadata (date, duration, participants)</li>
              <li>• User information for meeting hosts and call participants</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}